import React, { useState, useRef, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import classNames from 'classnames';
import { isValidEmail } from '../../utils/utils';
import { CheckInvitationForUser } from '../../connection/app-entry';
import {
	checkInvitation,
	setOnboardingRoute,
	createPlatformAccount,
} from '../../stores/actions/session';
import { ONBOARDING_ROUTES } from '../../stores/session-status';
import TextInput from '../general-components/text-input';
import LoadingSpinner from '../general-components/loading-spinner';
import TellyChannelLogo from '../../images/app-store-icon.png';
import { GreenroomState as GS } from '../../stores/types/types';

export default function CreateAccount() {
	const dispatch = useDispatch();

	const token = useSelector((state: GS) => state.SessionReducer.token);
	const joinLoading = useSelector((state: GS) => state.SessionReducer.joinLoading);
	const joinError = useSelector((state: GS) => state.SessionReducer.joinError);
	const displayVerifyEmail = useSelector((state: GS) => state.SessionReducer.displayVerifyEmail);
	const host = useSelector((state: GS) => state.SessionReducer.host);

	const [meetingId, setMeetingId] = useState<string>('');
	const [email, setEmail] = useState<string>('');
	const [disableEmail, setDisableEmail] = useState<boolean>(false);
	const [firstName, setFirstName] = useState<string>('');
	const [lastName, setLastName] = useState<string>('');
	const [error, setError] = useState<string>('');

	const verificationCode = useRef(undefined as string | undefined);

	useEffect(() => {
		// Check if user is logged in
		if (token) {
			if (meetingId) {
				// Create account form was opened from an invite presenter email link.
				// See if we should navigate user to a meeting.
				CheckInvitationForUser(meetingId, token)
					.then((invite) => {
						if (invite) {
							dispatch(checkInvitation(token, invite.code));
						} else {
							dispatch(setOnboardingRoute(ONBOARDING_ROUTES.PRESENTER_LOGIN));
						}
					});
			} else if (host) {
				dispatch(setOnboardingRoute(ONBOARDING_ROUTES.PRODUCER_SELECT_MEETING));
			} else {
				dispatch(setOnboardingRoute(ONBOARDING_ROUTES.ONBOARDING));
			}
		}
	}, [token, meetingId, checkInvitation, setOnboardingRoute]);

	useEffect(() => {
		if (meetingId && displayVerifyEmail) {
			setMeetingId('');
			const url = new URL(window.location.href);
			window.history.replaceState(null, '', url.href.split(`/${meetingId}`)[0]);
		}
	}, [meetingId, displayVerifyEmail]);

	useEffect(() => {
		// A verification code is appended to the create-account link in the email if:
		// - A presenter was invited to a GR and didn't have an account
		// - A producer was added with GR super admin and didn't have a verified presenter account
		// We grab it here and send it up to the API when the account is created to auto verify the user
		const url = new URL(window.location.href);

		// If the first path segment isn't 'create-account' or 'producer', it should be a meeting_id.
		// This will be used after the account is created to verify that the meeting exists
		// and the user is invited to it.
		const [firstPathSegment] = url.pathname.split('/').filter((item) => !!item);
		if (firstPathSegment !== 'create-account' && firstPathSegment !== 'producer') {
			setMeetingId(firstPathSegment);
		}

		const code = url.searchParams.get('verify');
		if (code) {
			verificationCode.current = code;
		}

		// URLSearchParams interprets + as a space.
		// We use url.href and split it to permit creating accounts that use special characters like +.
		const splitUrl = url.href.split('email=')[1] ? url.href.split('email=')[1] : null;
		if (splitUrl) {
			setEmail(email);
			setDisableEmail(true);
		}
	}, []);

	function handlePlatformCreateAccount(
		emailAddress: string,
		fName: string,
		lName: string,
	) {
		// Get the meeting ID from the URL
		const url = new URL(window.location.href);
		const lastPathSegment = url.pathname.split('/').filter((str) => str).pop();
		const roomId = lastPathSegment && lastPathSegment !== 'producer'
			? lastPathSegment
			: undefined;

		dispatch(createPlatformAccount(
			emailAddress,
			fName,
			lName,
			roomId,
			`${url.origin}/oauth/`,
		));
	}

	async function submitForm(e: React.FormEvent<HTMLFormElement>) {
		e.preventDefault();

		if (!isValidEmail(email)) {
			return setError('Please enter a valid email address.');
		}

		return handlePlatformCreateAccount(email, firstName, lastName);
	}

	function checkEmail(e: React.ChangeEvent<HTMLInputElement>) {
		setEmail(e.target.value);
	}

	function checkFirstName(e: React.ChangeEvent<HTMLInputElement>) {
		setFirstName(e.target.value);
	}

	function checkLastName(e: React.ChangeEvent<HTMLInputElement>) {
		setLastName(e.target.value);
	}

	function backToSignIn() {
		if (host) {
			dispatch(setOnboardingRoute(ONBOARDING_ROUTES.PRODUCER_LOGIN));
		} else {
			dispatch(setOnboardingRoute(ONBOARDING_ROUTES.PRESENTER_LOGIN));
		}
	}

	function showJoinError() {
		switch (joinError) {
			case 'exists': return 'An account with that email already exists';
			case 'input': return "It appears that your inputs aren't formatted correctly.";
			// This case is when a presenter clicks "forgot password?" and then no account exists for the
			// email they enter. So we we auto navigate the user to the create account form with this
			// message displayed when they arrive
			case 'noAccountForEmail': return 'No greenroom account found. Create an account to begin.';
			default: return 'An unknown error has occurred. Please try again.';
		}
	}

	return (
		<div className="panel teal-green sign-in">
			<div className="producer-logo-title create-account-page">
				<img src={TellyChannelLogo} style={{ width: '48px', marginBottom: '15px' }} alt={'Connected'} />
				<span className="title green create-account-page">Greenroom</span>
				<span className="sub-title">Create an account</span>

			</div>
			{!displayVerifyEmail
				? (
					<form onSubmit={submitForm} className="create-account-form">
						<TextInput
							onChange={checkEmail}
							placeholder={'Email'}
							value={email}
							type={'email'}
							required
							noAutocomplete={true}
							disabled={disableEmail}
							customClassNameProp="create-account-input"
						/>
						<TextInput
							onChange={checkFirstName}
							placeholder={'First Name'}
							value={firstName}
							noAutocomplete={true}
							customClassNameProp="create-account-input"
						/>
						<TextInput
							onChange={checkLastName}
							placeholder={'Last Name'}
							value={lastName}
							noAutocomplete={true}
							customClassNameProp="create-account-input"
						/>
						{joinError && (
							<div className="signin-error">
								{showJoinError()}
							</div>
						)}
						<div className={classNames('signin-error', { show: !!error })}>
							<p>{error}</p>
						</div>
						<button className="onboarding create-account" disabled={!email}>Create account{joinLoading && (
							<div style={{ width: 14, height: 14, marginLeft: 8 }}>
								<LoadingSpinner
									height={14}
									width={14}
									stroke={10}
								/>
							</div>
						)}</button>

						<div className="create-account">
							<span>Already have an account?</span><button onClick={backToSignIn} className="clickable">Log in</button>
						</div>
					</form>
				) : (
					<div className="producer-logo-title verify-account">
						<div className="message-container">
							<span>
								Your account has been created!
							</span>
							<span>
								Check your email to continue account creation.
							</span>
						</div>
					</div>
				)
			}
		</div>
	);
}
